import * as Sentry from "@sentry/vue";
import { AxiosError } from "axios";

import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { TCurrentUser } from "@/data/api/kili/current-user/current-user.type";
import { V4bApiError, V4bClientError } from "@/data/api/v4b/error/v4b-client-error.types";

interface ISentryHttpError {
  status?: number;
  requestPayload?: unknown;
  transactionId?: string;
  body?: V4bApiError | unknown;
}

function mapAxiosError(axiosError: AxiosError): Omit<ISentryHttpError, "body"> {
  return {
    status: axiosError.status,
    // Missing "Access-Control-Expose-Headers: X-Voltalis-Transaction-Id" in response header
    // Because of CORS, we wont be able to read this header for now
    transactionId: axiosError.response?.headers?.["x-voltalis-transaction-id"] ?? "-",
    requestPayload: {
      method: axiosError.request.__METHOD__,
      url: axiosError.request.__URL__,
      payload: axiosError.config?.data,
    },
  };
}

function handleKiliException(axiosError: AxiosError, err: V4bClientError): ISentryHttpError {
  return {
    ...mapAxiosError(axiosError),
    body: JSON.stringify(err),
  };
}

function handleHttpException(err: Error) {
  if (!(err instanceof AxiosError)) {
    return undefined;
  }

  const maybeResponseData = err.response?.data;

  if (maybeResponseData instanceof V4bClientError) {
    return handleKiliException(err, maybeResponseData);
  }

  return {
    ...mapAxiosError(err),
    body: JSON.stringify(err.response?.data),
  };
}

export function useSentry() {
  const { env } = useServices();
  const { VITE_SENTRY_ENABLED } = env;

  function captureException(error: Error) {
    if (!VITE_SENTRY_ENABLED) {
      return;
    }

    Sentry.withScope((scope) => {
      const httpException = handleHttpException(error);
      if (httpException) {
        scope.setTag("http_status", httpException.status);
        scope.setTag("x-voltalis-transaction-id", httpException.transactionId);

        scope.addBreadcrumb({
          category: "api",
          level: "error",
          message: error.message,
          data: {
            http: {
              transactionId: httpException.transactionId,
              status: httpException.status,
              response: httpException.body,
              request: httpException.requestPayload,
            },
          },
        });
      }

      scope.addBreadcrumb({
        category: "error",
        level: "error",
        message: error.message,
        data: {
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
        },
      });

      Sentry.captureException(error);
    });
  }

  function setUser(user?: TCurrentUser | null) {
    if (!VITE_SENTRY_ENABLED) {
      return;
    }

    if (!user) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      username: user.userName,
    });
  }

  return {
    sentry: Sentry,
    enabled: VITE_SENTRY_ENABLED,
    captureException,
    setUser,
  };
}
